import React, { useState, SyntheticEvent, useEffect } from 'react';
import { Modal, Button, Popconfirm, Select, Collapse, Switch } from 'antd';
import { ColorResult } from 'react-color';
import { Input } from 'antd';
import { DatasetVersion, DatasetSettingsData } from '../../../types/dataset';
import { LabelSetting } from '../../../types/label';
import { BlockedDiv } from '../../../components/BlockedDiv';
import { ModalSubTitle, ModalTitle } from '../../../components/text/Titles';
import { useGetAlgorithms } from '../../../lib/hooks';
import { LabelSettings } from './LabelSettings';
import { CopyOutlined } from '@ant-design/icons';
import { DatasetCopy } from './DatasetCopy';

const { Option } = Select;

type Props = {
  visible: boolean;
  onClose: () => any;
  onCommit: () => any;
  onSave: (s: DatasetSettingsData) => any;
  title: string;
  datasetVersion: null | DatasetVersion;
  commiting: boolean;
};

const DatasetSettings = (props: Props) => {
  const { visible, onClose, title, datasetVersion, onSave, onCommit, commiting } = props;
  const [labels, setLabels] = useState<LabelSetting[] | null>(null);
  const [selectorVisible, setSelectorVisible] = useState<boolean[]>([]);
  const [nameValue, setNameValue] = useState<string>('');
  const [commitPopup, setCommitPopup] = useState<boolean>(false);
  const [newDocumentsCount, setNewDocumentsCount] = useState<number>(0);
  const [selectedAlgorithm, setSelectedAlgorithm] = useState<string>('-1');
  const [ocrrefresh, setOcrrefresh] = useState<boolean>(false);

  const { algorithms } = useGetAlgorithms();

  useEffect(() => {
    setNewDocumentsCount(datasetVersion?.new_document_count || 0);
  }, [datasetVersion]);

  const setupInitialValues = () => {
    if (datasetVersion) {
      setLabels(datasetVersion.settings.annotationTypes);
      setSelectedAlgorithm(datasetVersion.settings.algorithmId || '-1');
      setSelectorVisible(Array(datasetVersion.settings.annotationTypes.length).fill(false));
      setOcrrefresh(datasetVersion.settings.ocrrefresh);
    }
  };

  if (datasetVersion && !labels) {
    setupInitialValues();
  }

  const closeModal = () => {
    setupInitialValues();
    onClose();
  };

  const handleModelChange = (value: string) => {
    setSelectedAlgorithm(value);
  };

  const onNameChange = (event: SyntheticEvent<HTMLInputElement>) => {
    setNameValue(event.currentTarget.value);
  };

  const showSelector = (i: number) => {
    if (labels) {
      let s = Array(labels.length).fill(false);
      if (i >= 0 && !selectorVisible[i]) {
        s[i] = true;
      }
      setSelectorVisible(s);
    }
  };

  const changeColor = (i: number, color: ColorResult) => {
    if (labels) {
      setLabels([
        ...labels.map((label, index) => {
          return index === i ? { ...label, color: color.hex } : label;
        }),
      ]);
      showSelector(i);
    }
  };

  const deleteLabel = (i: number) => {
    if (labels) {
      setLabels([...labels.filter((_, index) => index !== i)]);
      showSelector(-1);
    }
  };

  const switchHidden = (i: number) => {
    if (labels) {
      setLabels(
        [...labels].map((label, index) => {
          return index === i ? { ...label, hidden: !label.hidden } : label;
        })
      );
      showSelector(-1);
    }
  };

  const onCopy = (datasetVersion: DatasetVersion) => {
    if (datasetVersion.settings) {
      setLabels(datasetVersion.settings.annotationTypes);
      setSelectedAlgorithm(datasetVersion.settings.algorithmId || '-1');
      setSelectorVisible(Array(datasetVersion.settings.annotationTypes.length).fill(false));
    }
  };

  const showStatus = (status?: string) => {
    let possibleStatus = ['Not started', 'Ongoing', 'Done'];
    let possibleColors = ['#fafafa', '#fff7e6', '#F6FFED'];
    let possibleTxtColors = ['#000000a6', '#fa8c16', '#52C41A'];
    let index = 0;
    let bkgcol = '#fafafa';
    let textcol = '#000000a6';
    let text = 'Not started';
    if (status && newDocumentsCount === 0) {
      index = possibleStatus.indexOf(status);
      bkgcol = possibleColors[index];
      textcol = possibleTxtColors[index];
      text = status;
    }
    return [textcol, bkgcol, text];
  };

  const switchStatus = (i: number) => {
    if (labels) {
      let l = labels;
      let l_i = l[i];
      let possibleStatus = ['Not started', 'Ongoing', 'Done'];
      let index = 0;
      if (l_i.status && newDocumentsCount === 0) {
        index = possibleStatus.indexOf(l_i.status);
        index = (index + 1) % 3;
      }
      l_i.status = possibleStatus[index];
      l[i] = l_i;
      setLabels(l);
      showSelector(-1);
    }
  };

  const addLabel = () => {
    if (labels) {
      let newlabel = { name: nameValue, color: '#ff00ff', hidden: false, status: 'Not started' };
      setLabels([...labels, newlabel]);
      setNameValue('');
    }
  };

  const editLabel = (i: any, newName: string, shortcut?: string) => {
    if (labels) {
      setLabels([
        ...labels.map((label, index) => {
          return index === i ? { ...label, name: newName, shortcut: shortcut } : label;
        }),
      ]);
    }
  };

  const toggleCommitPopup = () => {
    setCommitPopup(!commitPopup);
  };

  return (
    <Modal
      destroyOnClose={true}
      style={{ minWidth: '620px' }}
      visible={visible}
      title={<ModalTitle>{title}</ModalTitle>}
      onCancel={closeModal}
      footer={
        <div>
          <Button
            type="default"
            onClick={e => {
              if (labels) onSave({ annotationTypes: labels, algorithmId: selectedAlgorithm, ocrrefresh: ocrrefresh });
            }}>
            Save
          </Button>
          <Button type="default" onClick={closeModal}>
            Cancel
          </Button>
        </div>
      }>
      <BlockedDiv>
        <Collapse ghost className="no-padding">
          <Collapse.Panel
            showArrow={true}
            header={
              <span>
                <CopyOutlined style={{ marginRight: 5 }} /> Copy settings from another dataset
              </span>
            }
            key="1">
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <DatasetCopy onCopy={onCopy} />
            </div>
          </Collapse.Panel>
        </Collapse>
      </BlockedDiv>
      <ModalSubTitle>Labels</ModalSubTitle>
      <BlockedDiv>
        {labels &&
          labels.map((type, i) => {
            return (
              <LabelSettings
                labels={labels}
                key={i}
                type={type}
                selector={selectorVisible[i]}
                showSelector={showSelector}
                changeColor={changeColor}
                deleteLabel={deleteLabel}
                editLabel={editLabel}
                switchHidden={switchHidden}
                switchStatus={switchStatus}
                showStatus={showStatus}
                i={i}
              />
            );
          })}
        <div style={{ marginTop: '20px' }}>
          <Input
            placeholder="New Label"
            value={nameValue}
            onChange={onNameChange}
            style={{ margin: '0px 10px 10px 0px', width: '150px' }}
          />
          <Button
            type="default"
            onClick={() => {
              if (nameValue) addLabel();
            }}>
            Add Label
          </Button>
        </div>
      </BlockedDiv>

      <ModalSubTitle>History</ModalSubTitle>
      <BlockedDiv>
        <Popconfirm
          title="Are you sure to commit a new version?"
          visible={commitPopup}
          onConfirm={() => {
            onCommit();
            toggleCommitPopup();
          }}
          onCancel={toggleCommitPopup}
          okText="Yes"
          cancelText="No">
          <Button type="primary" loading={commiting} onClick={toggleCommitPopup}>
            Commit This Version
          </Button>
        </Popconfirm>
      </BlockedDiv>

      <ModalSubTitle>Automatic Labelling</ModalSubTitle>
      <BlockedDiv>
        <p>Select a model:</p>
        <Select value={selectedAlgorithm} style={{ width: '100%', marginBottom: '20px' }} onChange={handleModelChange}>
          <Option key="-1" value="-1">
            No Inference
          </Option>
          {algorithms.map(algo => (
            <Option key={algo.id} value={algo.id}>
              {algo.name}
            </Option>
          ))}
        </Select>
      </BlockedDiv>
      <BlockedDiv>
        <div className="flex items-center gap-2">
          <Switch size="small" checked={ocrrefresh} onChange={() => setOcrrefresh(prev => !prev)} />
          <span>OCR Refresh active</span>
        </div>
      </BlockedDiv>
    </Modal>
  );
};

export default DatasetSettings;
